import { AxiosError } from "axios";
import { t } from "i18next";
import { toast, ToastOptions } from "react-toastify";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import { randomId } from "@/utils/UUtils";

import { TApiError } from "@/types";

// A message given by the caller wins; the API's own error message
// still shows when it has one, the caller's is the fallback.
const errorMessageOf = (error: unknown, fallback?: string) => {
	const axiosError = error as AxiosError<TApiError, unknown>;
	return axiosError?.response?.data?.message
		? getApiErrormessage(axiosError)
		: (fallback ?? getApiErrormessage(axiosError));
};

/**
 * A toast on failure only: for an action whose success is already visible
 * (a redirect, a busy button), where a pending/success toast is noise.
 */
export const errorToast = (promise: Promise<unknown>, fallback?: string) =>
	promise.catch((error: unknown) => {
		toast.error(errorMessageOf(error, fallback));
	});

export const promiseToast = (
	promise: Promise<unknown>,
	messages?: {
		pending?: string;
		success?: string;
		error?: string;
	},
	options?: ToastOptions
) => {
	const toastId = options?.toastId || randomId();

	if (options?.toastId)
		toast.update(toastId, {
			render: messages?.pending ?? t("promiseToast.pending"),
			type: "default",
			isLoading: true,
		});

	return toast.promise(
		promise,
		{
			pending: messages?.pending ?? t("promiseToast.pending"),
			success: messages?.success ?? t("promiseToast.success"),
			error: {
				render({ data: error }) {
					return errorMessageOf(error, messages?.error);
				},
			},
		},
		{
			...options,
			toastId,
		}
	);
};
